import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";

import config from "../../config";
import { getAuthToken } from "../../utils/authStorage";
import { chipSx } from "./attendance/zkStatusStyles";
import {
  HOLD_STATE_META,
  activateButtonSx,
  backButtonSx,
  cancelButtonSx,
  formatStatusLabel,
  getStatusChipSx,
  membershipStatusLabel,
} from "./hpcAdminStatusStyles";

const HOLD_TYPES = [
  { value: "medical", label: "Medical" },
  { value: "travel", label: "Travel" },
  { value: "personal", label: "Personal" },
];

// Status reason codes from the Member Status requirements (reason master table).
const REASON_CODES = [
  { value: "MED", label: "MED – Medical" },
  { value: "INJ", label: "INJ – Injury" },
  { value: "TRV", label: "TRV – Travel" },
  { value: "EXM", label: "EXM – Examination" },
  { value: "FIN", label: "FIN – Financial issue" },
  { value: "PAY", label: "PAY – Outstanding fees" },
  { value: "DIS", label: "DIS – Disciplinary action" },
  { value: "MIG", label: "MIG – Migration" },
  { value: "RES", label: "RES – Resigned" },
  { value: "DUP", label: "DUP – Duplicate record" },
  { value: "OTH", label: "OTH – Other" },
];

const EVENT_LABELS = {
  hold_scheduled: "Hold scheduled",
  hold_ended_early: "Hold ended early",
  hold_cancelled: "Hold cancelled",
  status_changed: "Status changed",
};

const EMPTY_CREATE = {
  hold_type: "medical", starts_on: "", planned_end_on: "", reason: "", reason_code: "", remarks: "", approved_by_note: "",
};
const EMPTY_ACTION = { reason: "", ended_on: "", remarks: "", approved_by_note: "" };
// Status changes from the profile take effect today; the API also accepts optional fields.
const EMPTY_STATUS = { status: "", reason: "" };

const authConfig = () => {
  const token = getAuthToken();
  return { headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) } };
};

// RFC 4122 v4 request id; reused while a dialog stays open so retries are idempotent.
const newRequestId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const apiMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (error?.response?.status === 401) return "Your session has expired. Please log in again.";
  let message = data?.error || fallback;
  if (data?.details?.affected_hold_ids?.length) {
    message += ` Affected holds: ${data.details.affected_hold_ids.map((id) => `#${id}`).join(", ")}.`;
  }
  if (data?.details?.mismatched_fields?.length) {
    message += ` Different fields: ${data.details.mismatched_fields.join(", ")}.`;
  }
  return message;
};

const orDash = (value) => (value === null || value === undefined || value === "" ? "—" : value);

const compact = (values) =>
  Object.fromEntries(Object.entries(values).filter(([, value]) => value !== "" && value !== null && value !== undefined));

const Summary = ({ label, children }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Box>{children}</Box>
  </Grid>
);

const HpcMemberHolds = ({ registration, onChanged }) => {
  const registrationId = registration?.id;
  const [holds, setHolds] = useState([]);
  const [membership, setMembership] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [action, setAction] = useState(null); // { kind: 'end' | 'cancel', hold }
  const [actionForm, setActionForm] = useState(EMPTY_ACTION);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const requestIdRef = useRef(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusForm, setStatusForm] = useState(EMPTY_STATUS);
  const statusRequestIdRef = useRef(null);

  const base = `${config.apiBaseUrl}/hpc/admin/registrations/${registrationId}`;

  const load = useCallback(async () => {
    if (!registrationId) return;
    setLoading(true);
    setLoadError("");
    try {
      const [holdsResponse, historyResponse] = await Promise.all([
        axios.get(`${base}/holds`, authConfig()),
        axios.get(`${base}/status-history`, { ...authConfig(), params: { limit: 50 } }),
      ]);
      setHolds(holdsResponse.data.holds || []);
      setMembership(holdsResponse.data.membership || null);
      setHistory(historyResponse.data.history || []);
    } catch (error) {
      setLoadError(apiMessage(error, "Could not load membership holds."));
    } finally {
      setLoading(false);
    }
  }, [base, registrationId]);

  // Reload whenever the profile reloads the registration (activation, clearance, payment,
  // status change) so the summary and the allowed status changes are never stale.
  const reloadKey = `${registration?.status}|${registration?.expiry_date}|${registration?.updated_at}`;
  useEffect(() => { load(); }, [load, reloadKey]);

  const afterChange = async (message) => {
    toast.success(message);
    await load();
    if (onChanged) await onChanged();
  };

  const openCreate = () => {
    requestIdRef.current = newRequestId();
    setCreateForm(EMPTY_CREATE);
    setFormError("");
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    const { hold_type: holdType, starts_on: startsOn, planned_end_on: plannedEnd, reason } = createForm;
    if (!holdType || !startsOn || !plannedEnd || !reason.trim()) {
      setFormError("Hold type, start date, planned end date and reason are required.");
      return;
    }
    if (plannedEnd < startsOn) {
      setFormError("The planned end date must be on or after the start date.");
      return;
    }
    setSubmitting(true);
    setFormError("");
    try {
      await axios.post(`${base}/holds`, compact({ ...createForm, request_id: requestIdRef.current }), authConfig());
      setCreateOpen(false);
      await afterChange("Hold scheduled");
    } catch (error) {
      setFormError(apiMessage(error, "Could not create the hold."));
    } finally {
      setSubmitting(false);
    }
  };

  const openAction = (kind, hold) => {
    setAction({ kind, hold });
    setActionForm(EMPTY_ACTION);
    setFormError("");
  };

  const submitAction = async () => {
    if (!actionForm.reason.trim()) {
      setFormError("A reason is required.");
      return;
    }
    const { kind, hold } = action;
    const body = compact(kind === "end" ? actionForm : { ...actionForm, ended_on: "" });
    setSubmitting(true);
    setFormError("");
    try {
      await axios.post(`${base}/holds/${hold.id}/${kind}`, body, authConfig());
      setAction(null);
      await afterChange(kind === "end" ? "Hold ended early" : "Hold cancelled");
    } catch (error) {
      setFormError(apiMessage(error, kind === "end" ? "Could not end the hold." : "Could not cancel the hold."));
    } finally {
      setSubmitting(false);
    }
  };

  const allowedChanges = membership?.allowed_status_changes || [];
  const blockedChanges = membership?.status_change_blocks || {};

  const openStatus = () => {
    statusRequestIdRef.current = newRequestId();
    setStatusForm({ ...EMPTY_STATUS, status: allowedChanges[0] || "" });
    setFormError("");
    setStatusOpen(true);
  };

  const submitStatus = async () => {
    if (!statusForm.status || !statusForm.reason.trim()) {
      setFormError("A new status and a reason are required.");
      return;
    }
    setSubmitting(true);
    setFormError("");
    try {
      await axios.post(`${base}/status`, {
        status: statusForm.status, reason: statusForm.reason.trim(), request_id: statusRequestIdRef.current,
      }, authConfig());
      setStatusOpen(false);
      await afterChange(`Status changed to ${membershipStatusLabel(statusForm.status)}`);
    } catch (error) {
      setFormError(apiMessage(error, "Could not change the status."));
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = (key) => (event) => setStatusForm((form) => ({ ...form, [key]: event.target.value }));

  const effectiveStatus = membership?.effective_status;
  const canCreate = effectiveStatus === "active" && membership?.expiry_known;
  const createBlockedReason = !membership
    ? ""
    : !membership.expiry_known
      ? "Holds are unavailable because this membership has no recorded expiry date."
      : effectiveStatus !== "active"
        ? `Holds can only be created for active members (current: ${membershipStatusLabel(effectiveStatus)}).`
        : "";

  const updateCreate = (key) => (event) => setCreateForm((form) => ({ ...form, [key]: event.target.value }));
  const updateAction = (key) => (event) => setActionForm((form) => ({ ...form, [key]: event.target.value }));

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="h5" fontWeight="bold">Membership Status &amp; Holds</Typography>
          <Box display="flex" gap={1}>
            <Button variant="outlined" size="small" sx={backButtonSx} onClick={load} disabled={loading}>
              Refresh
            </Button>
            <Button variant="outlined" size="small" sx={backButtonSx} onClick={openStatus}
              disabled={!membership || loading}>
              Change Status
            </Button>
            <Button variant="contained" size="small" sx={activateButtonSx} onClick={openCreate}
              disabled={!canCreate || loading}>
              Add Hold
            </Button>
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />

        {loading && !membership && (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
        )}
        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
            {loadError}
          </Alert>
        )}

        {membership && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Summary label="Stored status">
                <Chip size="small" label={formatStatusLabel(membership.stored_status)} sx={getStatusChipSx(membership.stored_status)} />
              </Summary>
              <Summary label="Current (effective) status">
                <Chip size="small" label={membershipStatusLabel(effectiveStatus)} sx={getStatusChipSx(effectiveStatus)} />
              </Summary>
              <Summary label="Membership status (admin)">
                <Typography variant="body1">
                  {membership.membership_label ? membershipStatusLabel(membership.membership_label) : "—"}
                  {membership.admin_status ? "" : " (no admin status set)"}
                </Typography>
              </Summary>
              <Summary label="Frozen days credited">
                <Typography variant="body1">{membership.frozen_days_credited ?? 0}</Typography>
              </Summary>
              <Summary label="Stored expiry date">
                <Typography variant="body1">{orDash(membership.stored_expiry_date)}</Typography>
              </Summary>
              <Summary label="Effective expiry date">
                <Typography variant="body1">
                  {membership.expiry_known ? orDash(membership.effective_expiry_date) : "Not recorded"}
                </Typography>
              </Summary>
            </Grid>
            {createBlockedReason && <Alert severity="info" sx={{ mb: 2 }}>{createBlockedReason}</Alert>}
          </>
        )}

        <Typography variant="h6" sx={{ mb: 1 }}>Holds</Typography>
        {holds.length === 0 && !loading ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No holds recorded.</Typography>
        ) : (
          <Box sx={{ overflowX: "auto", mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell><TableCell>Type</TableCell><TableCell>Start</TableCell>
                  <TableCell>Planned end</TableCell><TableCell>Last frozen day</TableCell><TableCell>State</TableCell>
                  <TableCell>Reason</TableCell><TableCell>Created by</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {holds.map((hold) => {
                  const meta = HOLD_STATE_META[hold.state] || { label: hold.state, color: "#9e9e9e" };
                  return (
                    <TableRow key={hold.id}>
                      <TableCell>{hold.id}</TableCell>
                      <TableCell>{formatStatusLabel(hold.hold_type)}</TableCell>
                      <TableCell>{hold.starts_on}</TableCell>
                      <TableCell>{hold.planned_end_on}</TableCell>
                      <TableCell>{orDash(hold.effective_end_on)}</TableCell>
                      <TableCell><Chip size="small" label={meta.label} sx={chipSx(meta.color)} /></TableCell>
                      <TableCell>{hold.reason_code ? `${hold.reason_code} – ` : ""}{hold.reason}</TableCell>
                      <TableCell>{hold.created_by}</TableCell>
                      <TableCell align="right">
                        {hold.state === "effective" && (
                          <Button size="small" onClick={() => openAction("end", hold)}>End early</Button>
                        )}
                        {hold.state === "scheduled" && (
                          <Button size="small" color="error" onClick={() => openAction("cancel", hold)}>Cancel</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        <Typography variant="h6" sx={{ mb: 1 }}>Status History</Typography>
        {history.length === 0 && !loading ? (
          <Typography variant="body2" color="text.secondary">No status changes recorded.</Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Recorded</TableCell><TableCell>Event</TableCell><TableCell>Status change</TableCell>
                  <TableCell>Effective date</TableCell><TableCell>Reason</TableCell><TableCell>Changed by</TableCell>
                  <TableCell>Approval note (unverified)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>{event.recorded_at}</TableCell>
                    <TableCell>{EVENT_LABELS[event.event_type] || event.event_type}{event.hold_id ? ` (#${event.hold_id})` : ""}</TableCell>
                    <TableCell>
                      {event.from_status || event.to_status
                        ? `${event.from_status ? membershipStatusLabel(event.from_status) : "—"} → ${event.to_status ? membershipStatusLabel(event.to_status) : "—"}`
                        : "—"}
                    </TableCell>
                    <TableCell>{event.effective_date}</TableCell>
                    <TableCell>{event.reason_code ? `${event.reason_code} – ` : ""}{orDash(event.reason)}</TableCell>
                    <TableCell>{orDash(event.actor)}</TableCell>
                    <TableCell>{orDash(event.approved_by_note)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>

      <Dialog open={createOpen} onClose={() => !submitting && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add membership hold</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The start and planned end dates are both frozen days. Frozen days that are credited extend the
            membership expiry; the stored expiry date is not changed.
          </Typography>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Hold type" value={createForm.hold_type} onChange={updateCreate("hold_type")}>
                {HOLD_TYPES.map((type) => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Reason code (optional)" value={createForm.reason_code} onChange={updateCreate("reason_code")}>
                <MenuItem value="">None</MenuItem>
                {REASON_CODES.map((code) => <MenuItem key={code.value} value={code.value}>{code.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Start date" InputLabelProps={{ shrink: true }}
                value={createForm.starts_on} onChange={updateCreate("starts_on")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Planned end date (last frozen day)" InputLabelProps={{ shrink: true }}
                value={createForm.planned_end_on} onChange={updateCreate("planned_end_on")} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label="Reason" value={createForm.reason} onChange={updateCreate("reason")}
                inputProps={{ maxLength: 2000 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Remarks (optional)" value={createForm.remarks}
                onChange={updateCreate("remarks")} inputProps={{ maxLength: 2000 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Approval note (optional)" value={createForm.approved_by_note}
                onChange={updateCreate("approved_by_note")} inputProps={{ maxLength: 255 }}
                helperText="For reference only; this is not a verified approval." />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button sx={cancelButtonSx} onClick={() => setCreateOpen(false)} disabled={submitting}>Close</Button>
          <Button variant="contained" sx={activateButtonSx} onClick={submitCreate} disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
            Save hold
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(action)} onClose={() => !submitting && setAction(null)} fullWidth maxWidth="sm">
        <DialogTitle>{action?.kind === "end" ? "End hold early" : "Cancel scheduled hold"}</DialogTitle>
        <DialogContent>
          {action && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Hold #{action.hold.id} ({formatStatusLabel(action.hold.hold_type)}, {action.hold.starts_on} to {action.hold.planned_end_on}).
              {action.kind === "end"
                ? " Leave the end date blank to make yesterday the last frozen day (today if the hold started today)."
                : " Only holds that have not started can be cancelled."}
            </Typography>
          )}
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2}>
            {action?.kind === "end" && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="date" label="Last frozen day (optional)" InputLabelProps={{ shrink: true }}
                  value={actionForm.ended_on} onChange={updateAction("ended_on")} />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth required label="Reason" value={actionForm.reason} onChange={updateAction("reason")}
                inputProps={{ maxLength: 2000 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Remarks (optional)" value={actionForm.remarks}
                onChange={updateAction("remarks")} inputProps={{ maxLength: 2000 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Approval note (optional)" value={actionForm.approved_by_note}
                onChange={updateAction("approved_by_note")} inputProps={{ maxLength: 255 }}
                helperText="For reference only; this is not a verified approval." />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button sx={cancelButtonSx} onClick={() => setAction(null)} disabled={submitting}>Close</Button>
          <Button variant="contained" color={action?.kind === "cancel" ? "error" : "primary"} onClick={submitAction}
            disabled={submitting} startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
            {action?.kind === "end" ? "End hold" : "Cancel hold"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={statusOpen} onClose={() => !submitting && setStatusOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Change membership status</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current status: <strong>{membershipStatusLabel(effectiveStatus)}</strong>. The change takes effect
            today. Greyed-out statuses show why they are not available now. Frozen and Expired follow the
            hold dates and membership expiry. For a frozen member, choosing Active ends the current hold
            (yesterday becomes the last frozen day); to record a different return date use "End early" on the hold.
          </Typography>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          {allowedChanges.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>No status change is possible right now; see the reasons below.</Alert>
          )}
          <TextField select fullWidth label="New status" value={statusForm.status} onChange={updateStatus("status")}
            sx={{ mb: 2 }}>
            {allowedChanges.map((status) => (
              <MenuItem key={status} value={status}>{membershipStatusLabel(status)}</MenuItem>
            ))}
            {Object.entries(blockedChanges).map(([status, reason]) => (
              <MenuItem key={status} value={status} disabled sx={{ display: "block", whiteSpace: "normal" }}>
                <Typography variant="body2">{membershipStatusLabel(status)}</Typography>
                <Typography variant="caption" color="text.secondary">{reason}</Typography>
              </MenuItem>
            ))}
          </TextField>
          <TextField fullWidth required label="Reason" value={statusForm.reason} onChange={updateStatus("reason")}
            inputProps={{ maxLength: 2000 }} />
        </DialogContent>
        <DialogActions>
          <Button sx={cancelButtonSx} onClick={() => setStatusOpen(false)} disabled={submitting}>Close</Button>
          <Button variant="contained" sx={activateButtonSx} onClick={submitStatus}
            disabled={submitting || !statusForm.status || !statusForm.reason.trim()}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default HpcMemberHolds;
